import type {
	MapboxLayer, MapboxSource, QueryFeature
} from '@/components/mapbox/types'
import { useErrorReporter } from '@/composables/useErrorReporter'
import * as turf from '@turf/turf'
import type { Feature, Geometry, Point } from 'geojson'
import type * as mapboxgl from 'mapbox-gl'
import type { LngLatLike, Map as MapboxMap } from 'mapbox-gl'
import { useMapboxState } from './useMapboxState'

export function useMapboxFeatures(mapId: string) {
	const { handleMapClick, handleMapHover } = useMapboxState(mapId)
	const { bugsnagReport } = useErrorReporter()

	// Cache loaded images so styleimagemissing can re-add them synchronously
	const imageCache = new Map<string, HTMLImageElement | ImageBitmap>()

	// Load icons for the map — returns a Promise so callers can await before adding layers
	const loadIcons = (map: MapboxMap, icons: { id: string
		url: string
		sdf?: boolean }[]): Promise<void> => {
		return Promise.all(icons.map(icon => new Promise<void>(resolve => {
			if (map.hasImage(icon.id)) {
				resolve()
				return
			}
			// Use cached image data for synchronous add (avoids async gap on re-adds)
			const cached = imageCache.get(icon.id)
			if (cached) {
				map.addImage(icon.id, cached, { sdf: !!icon.sdf })
				resolve()
				return
			}
			map.loadImage(icon.url, (error, image) => {
				if (error) {
					bugsnagReport({
						error: error || `Failed to load image ${icon.id}:`,
						showToast: false
					})
					resolve()
					return
				}
				if (image) {
					imageCache.set(icon.id, image)
					if (!map.hasImage(icon.id)) {
						map.addImage(icon.id, image, { sdf: !!icon.sdf })
					}
				}
				resolve()
			})
		}))).then(() => undefined)
	}

	// Synchronously re-add an icon from cache; returns true if cache hit
	const addImageFromCache = (map: MapboxMap, icon: { id: string; sdf?: boolean }): boolean => {
		const cached = imageCache.get(icon.id)
		if (cached) {
			if (!map.hasImage(icon.id)) {
				map.addImage(icon.id, cached, { sdf: !!icon.sdf })
			}
			return true
		}
		return false
	}

	// Update GeoJSON data for all sources
	const updateGeoJsonData = (map: MapboxMap, sources: MapboxSource[]) => {
		sources.forEach(source => {
			try {
				if (!map.getSource(source.id)) {
					map.addSource(source.id, source.source)
					// Store sources on map instance for reference in case of style reload
					if (!map.__mbxSources) {
						map.__mbxSources = {}
					}
					map.__mbxSources[source.id] = source.source
				} else {
					// Update existing source if it's GeoJSON
					const existingSource = map.getSource(source.id) as mapboxgl.GeoJSONSource
					if (existingSource && existingSource.setData && source.source.type === 'geojson' && source.source.data) {
						existingSource.setData(source.source.data)
					}
				}
			} catch (error) {
				if (!(error as Error)?.message?.includes('Style is not done loading')) {
					bugsnagReport({
						error: error as Error || new Error(`Failed to update source: ${source.id}`),
						showToast: false
					})
				}
			}
		})
	}

	// Set up map event handlers
	const setupMapEventHandlers = (map: MapboxMap, layers: MapboxLayer[]) => {
		const layerIds = layers.map(layer => layer.id)

		// Set cursor to pointer when hovering over features
		map.on('mouseenter', layerIds, e => {
			if (e.features && e.features.length > 0) {
				map.getCanvas().style.cursor = 'pointer'
				handleMapHover(e.features)
			}
		})

		// Reset cursor when leaving features
		map.on('mouseleave', layerIds, () => {
			map.getCanvas().style.cursor = ''
			handleMapHover(undefined)
		})

		// Handle click events on features
		map.on('click', layerIds, e => {
			if (e.features && e.features.length > 0) {
				const feature = e.features[0]
				const clusterId = feature?.properties?.cluster_id

				// Handle cluster click - zoom in to expand cluster
				if (clusterId && feature.source) {
					const source = map.getSource(feature.source) as mapboxgl.GeoJSONSource
					source.getClusterExpansionZoom(clusterId, (err, zoom) => {
						if (err) {
							bugsnagReport({
								error: err instanceof Error ? err : new Error('Error getting cluster expansion zoom'),
								showToast: false
							})
							return
						}
						const geometry = feature.geometry as Point
						if (geometry?.coordinates) {
							map.easeTo({
								center: geometry.coordinates as LngLatLike,
								zoom: zoom ?? map.getZoom() + 1
							})
						}
					})
					return
				}

				handleMapClick(e.features)
			}
		})

		// Reset click state when clicking on empty map
		map.on('click', e => {
			// Check if click was on a feature
			const features = map.queryRenderedFeatures(e.point, { layers: layerIds })

			if (features.length === 0) {
				handleMapClick(undefined)
			}
		})
	}

	// Check if a feature is a marker (point)
	const isMarkerEvent = (features: QueryFeature[] | undefined, layers: MapboxLayer[]) => {
		if (!features?.length) {
			return false
		}

		const markerLayers = layers.filter(
			layer => layer.layer.type === 'circle' || layer.layer.type === 'symbol'
		)

		return features.some(feature =>
			markerLayers.some(layer => feature?.layer?.id === layer.id)
		)
	}

	// Check if a feature is a polygon
	const isPolygonEvent = (features: QueryFeature[] | undefined, layers: MapboxLayer[]) => {
		if (!features?.length) {
			return false
		}

		const polygonLayers = layers.filter(layer => layer.layer.type === 'fill')

		return features.some(feature =>
			polygonLayers.some(layer => feature?.layer?.id === layer.id)
		)
	}

	// Check if a feature is a line
	const isLineEvent = (features: QueryFeature[] | undefined, layers: MapboxLayer[]) => {
		if (!features?.length) {
			return false
		}

		const lineLayers = layers.filter(layer => layer.layer.type === 'line')

		return features.some(feature =>
			lineLayers.some(layer => feature?.layer?.id === layer.id)
		)
	}

	// Calculate center of a feature
	const getFeatureCenter = (feature: Feature<Geometry>) => {
		if (feature.geometry.type === 'Point') {
			return feature.geometry.coordinates
		} else if (feature.geometry.type === 'LineString') {
			return turf.center(feature).geometry.coordinates
		} else if (feature.geometry.type === 'Polygon') {
			return turf.centroid(feature).geometry.coordinates
		}
		return [ 0, 0 ]
	}

	return {
		loadIcons,
		addImageFromCache,
		updateGeoJsonData,
		setupMapEventHandlers,
		isMarkerEvent,
		isPolygonEvent,
		isLineEvent,
		getFeatureCenter
	}
}

// Add custom property to mapboxgl.Map interface
declare module 'mapbox-gl' {
	interface Map {
		__mbxSources?: Record<string, any>
	}
}
